import { useState, useEffect, useCallback, useRef } from 'react'
import { request, fetchRaw } from '@papan/api-client'
import { USER_UI_SETTING_KEYS } from '@papan/types'

// BroadcastChannel for cross-tab sync
const SETTINGS_CHANNEL = 'papan-settings'

/**
 * Theme options: dark, dim (middle), light
 */
export type ThemeMode = 'dark' | 'dim' | 'light'

/**
 * Theme settings for different areas of the app
 */
export interface ThemeSettings {
  // Sync all areas to the same theme
  syncAll: boolean
  // Individual area themes (used when syncAll is false)
  drive: ThemeMode
  workspace: ThemeMode
  chat: ThemeMode
}

/**
 * A person's own UI preferences.
 *
 * Stored on the `users.settings` JSONB column since issue #369. They used to
 * live in a `settings.json` FILE inside the person's personal tenant, which
 * made saving a theme the last write an identity-only tenant had to permit —
 * and which is where #245 came from, since the whole object was PUT back from
 * a stale closure on every change.
 *
 * The wire shape is now a PATCH of only what changed. The blob on the server
 * also carries non-UI keys (`allowSubagentModelUpgrade`), which is why the
 * adapter below projects rather than casts.
 */
export interface PublicSettings {
  /**
   * ⚠ INERT since issue #369, lane A, A2.3 — every user is reset to English.
   *
   * Nothing writes it (the server's whitelist does not accept it and the column
   * does not store it), nothing projects it out of the server blob, and nothing
   * feeds it to i18next any more. The language is pinned at the i18next init —
   * see the TEMPORARY block in `shared/i18n/src/index.ts`, which is where
   * restoring this would begin.
   *
   * The field survives only because `SettingsView`'s Language page still reads
   * it and that file belongs to another lane; it goes when the page does. Do not
   * build anything new on it.
   */
  language: string
  theme: ThemeSettings

  // UI preferences
  showHiddenFiles: boolean
  defaultView: 'list' | 'grid'

  // Editor preferences
  fontSize: number
  tabSize: number
  /** Format Document on every save in the text editors (#987). Off by default. */
  formatOnSave: boolean
  /** Preferred editor per extension, e.g. { xlsx: 'onlyoffice-sheet' } */
  preferredEditors: Record<string, string>
  /** Last-used HTML-export options (enum/number values only — no ids/paths).
   *  Kept as an open record so shared/core-ui needn't depend on features/. */
  exportDefaults?: Record<string, string | number | boolean>
}

const defaultSettings: PublicSettings = {
  language: 'en',
  theme: {
    syncAll: true,
    drive: 'dim',
    workspace: 'dim',
    chat: 'dim',
  },
  showHiddenFiles: false,
  defaultView: 'list',
  fontSize: 14,
  tabSize: 2,
  formatOnSave: false,
  preferredEditors: {},
}

/**
 * Get the effective theme for an area
 */
export function getThemeForArea(settings: PublicSettings, area: 'drive' | 'workspace' | 'chat'): ThemeMode {
  if (settings.theme.syncAll) {
    // When synced, use workspace theme as the master
    return settings.theme.workspace
  }
  return settings.theme[area]
}

// Settings persist on `users.settings` via GET/PATCH /api/users/me/settings.
/**
 * Local mirror of JUST the theme.
 *
 * Settings live server-side, which means an ANONYMOUS visitor has
 * nowhere to keep them: the PATCH 401s and `savePatch` swallows it, so a theme they pick
 * survives until the next reload and no further. That is fine for the app, where you
 * are always signed in — but the SHARE-VIEW is mostly anonymous, and a theme toggle
 * that forgets on reload is a toggle that doesn't work.
 *
 * So the theme (and ONLY the theme — no other settings key is mirrored) is also kept in
 * localStorage, and seeds the initial state.
 *
 * Written for EVERY caller, not just anonymous ones. An earlier pass gated it on the
 * server write failing, to avoid "leaving an authenticated user's theme on the machine"
 * — but that bought nothing: ThemeProvider already caches the RESOLVED background under
 * `papan:theme-bg` for every user, ungated, so the device already reveals the same fact.
 * All the gate achieved was making the two keys disagree, and making an authenticated
 * user paint in the last anonymous visitor's theme until the server load landed.
 *
 * So: one device-local record of "what theme is this browser showing", kept in step with
 * the bg cache, seeded from on boot, and overwritten by the server whenever the server
 * has a value. A theme is a display preference, not a secret — it is stored as one.
 */
const THEME_LS_KEY = 'papan:theme'

function readLocalTheme(): ThemeSettings | null {
  try {
    const raw = localStorage.getItem(THEME_LS_KEY)
    if (!raw) return null
    const t = JSON.parse(raw) as Partial<ThemeSettings>
    // Shape-check: a hand-edited or stale value must not poison the theme class.
    const ok = (v: unknown): v is ThemeMode => v === 'dark' || v === 'dim' || v === 'light'
    if (!ok(t.drive) || !ok(t.workspace) || !ok(t.chat) || typeof t.syncAll !== 'boolean') return null
    return { syncAll: t.syncAll, drive: t.drive, workspace: t.workspace, chat: t.chat }
  } catch {
    return null
  }
}

function writeLocalTheme(theme: ThemeSettings): void {
  try { localStorage.setItem(THEME_LS_KEY, JSON.stringify(theme)) } catch { /* storage blocked */ }
}

/**
 * Project the server's blob down to the UI keys — and answer `null` when it
 * holds NONE of them.
 *
 * ── This function is why no backfill was needed ─────────────────────────
 *
 * `GET /api/users/me/settings` answers **200 with a truthy `{}`** for a user
 * who has never saved anything, and 200 with `{allowSubagentModelUpgrade:true}`
 * for one who has only ever touched that unrelated toggle. Either value, taken
 * at face value, sends the loader down its SERVER branch — which resolves to
 * `defaultSettings` and then OVERWRITES the device's theme mirror with the
 * default. Every existing user would lose their theme on the server *and* on
 * their machine, on first load, silently: strictly worse than what backfilling
 * out of `settings.json` would have avoided.
 *
 * So "empty" has to mean **empty of UI preferences**, not empty of the blob. A
 * naive `Object.keys(settings).length` check is the exact trap — that key is
 * already in production, so it would classify every such user as "has
 * settings".
 *
 * The projection list is `USER_UI_SETTING_KEYS` from `@papan/types` — the same
 * list the server's whitelist is written against — rather than
 * `Object.keys(defaultSettings)`, which still contains `language`. `language`
 * is being reset, not relocated (Phase 4), and the server stores it for nobody;
 * projecting it would let a stale value from some other source count as
 * "this user has settings".
 *
 * EXPORTED for its own tests. The loader carries two further defences against
 * the same loss — it seeds `theme` from the mirror before spreading the server
 * blob, and it writes the mirror back only from a theme the server actually
 * returned — so all three are individually correct and mutually redundant, and
 * no black-box case can fail when only ONE of them is broken. Testing the
 * classification directly is the only way to pin this one on its own terms.
 */
export function projectUiSettings(raw: unknown): Partial<PublicSettings> | null {
  if (typeof raw !== 'object' || raw === null) return null
  const blob = raw as Record<string, unknown>
  const out: Record<string, unknown> = {}
  for (const key of USER_UI_SETTING_KEYS) {
    if (key in blob && blob[key] !== undefined) out[key] = blob[key]
  }
  return Object.keys(out).length > 0 ? (out as Partial<PublicSettings>) : null
}

async function fetchSettings(): Promise<Partial<PublicSettings> | null> {
  try {
    const res = await fetchRaw('/api/users/me/settings')
    if (!res.ok) return null
    const { settings } = await res.json() as { settings?: unknown }
    return projectUiSettings(settings)
  } catch {
    return null
  }
}

/**
 * Persist ONLY what changed.
 *
 * The client half of #245. The old shape read the whole settings object out of
 * a closure and PUT it back, so a theme change in one tab silently reverted
 * whatever a second writer had saved in between — no error, no conflict, just
 * the stale value winning. A single-key PATCH cannot do that, and the server's
 * merge is in SQL, so two disjoint patches cannot lose each other either.
 */
async function savePatch(patch: Partial<PublicSettings>): Promise<boolean> {
  try {
    await request('/api/users/me/settings', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    })
    return true
  } catch {
    return false
  }
}

export function useSettings() {
  const [settings, setSettings] = useState<PublicSettings>(() => {
    const local = readLocalTheme()
    return local ? { ...defaultSettings, theme: local } : defaultSettings
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const channelRef = useRef<BroadcastChannel | null>(null)

  // Setup cross-tab sync via BroadcastChannel
  useEffect(() => {
    const channel = new BroadcastChannel(SETTINGS_CHANNEL)
    channelRef.current = channel

    /**
     * A PATCH, not the blob.
     *
     * Repointing the fetch does not fix this half: the channel used to carry
     * the whole settings object and the receiver REPLACED its state with it, so
     * a tab that had just changed something else had that change overwritten by
     * a sibling's stale copy — #245 again, one layer over. Merging a patch
     * cannot do that.
     */
    channel.onmessage = (event) => {
      const patch = event.data as Partial<PublicSettings>
      if (!patch || typeof patch !== 'object') return
      setSettings((prev) => ({ ...prev, ...patch }))

      // The device mirror is per-BROWSER, not per-tab, so a theme changed in a
      // sibling has to land here too — otherwise this tab reloads into the
      // theme it was showing before the change.
      if (patch.theme) writeLocalTheme(patch.theme)
    }

    return () => {
      channel.close()
      channelRef.current = null
    }
  }, [])

  // Broadcast the CHANGED KEYS to other tabs — never the whole object.
  const broadcastSettings = useCallback((patch: Partial<PublicSettings>) => {
    channelRef.current?.postMessage(patch)
  }, [])

  // Load settings on mount
  useEffect(() => {
    async function load() {
      setLoading(true)
      const loaded = await fetchSettings()
      if (loaded) {
        // Merge with defaults to handle missing keys. The blob is SPARSE — a
        // user who has only ever changed their font size has exactly that one
        // key — so the local theme mirror, not the default, is what fills the
        // theme when the server does not carry one.
        const merged = {
          ...defaultSettings,
          theme: readLocalTheme() ?? defaultSettings.theme,
          ...loaded,
        }
        setSettings(merged)

        // The server wins WHERE IT HAS A VALUE, so the mirror is replaced only
        // from a theme the server actually returned — otherwise the device
        // record would keep whatever was last set locally and could seed a
        // theme the user has since changed elsewhere.
        //
        // Guarded rather than unconditional: `merged.theme` is the DEFAULT for
        // a user whose blob holds other keys but no theme, and writing that
        // back is the same silent theme loss `projectUiSettings` exists to
        // prevent, one layer in. Only reachable because the blob is sparse —
        // the `settings.json` this replaced always held a whole object.
        if (loaded.theme) writeLocalTheme(loaded.theme)

        // NO i18n sync here, deliberately. `language` is not in
        // `USER_UI_SETTING_KEYS`, the column does not store it, and the language
        // is now pinned at the i18next init — it was RESET to English, not
        // relocated (#369 A2.3). A settings-driven `changeLanguage` here would
        // be a second authority over something that no longer has a first one.
      } else {
        // No settings file yet — a fresh account, or an ANONYMOUS visitor (the share
        // view). Keep whatever theme the local mirror seeded (readLocalTheme); defaults
        // otherwise. The server stays authoritative wherever it HAS a value: the branch
        // above overwrites this one, never the other way round.
        // `language` is not overridden here any more — `defaultSettings` already
        // carries `'en'`, which is the only language there is (#369 A2.3).
        const initialSettings = {
          ...defaultSettings,
          theme: readLocalTheme() ?? defaultSettings.theme,
        }
        setSettings(initialSettings)
      }
      setLoading(false)
    }
    load()
  }, [])

  /**
   * Apply, broadcast and persist a patch.
   *
   * `setSettings` is FUNCTIONAL rather than a spread of the `settings` closure:
   * the closure is the stale-read half of #245, and a hook that had re-rendered
   * from a sibling tab's broadcast between render and click would otherwise
   * throw that sibling's change away locally even though the wire is now a
   * patch.
   */
  const applyPatch = useCallback(async (patch: Partial<PublicSettings>) => {
    setSettings((prev) => ({ ...prev, ...patch }))

    // No `changeLanguage` here either. `updateSetting('language', …)` still
    // COMPILES — the Language page in SettingsView belongs to another lane and
    // still calls it — and it is now a local no-op: the key is dropped by the
    // server's whitelist and the language is pinned at the i18next init.
    // That is the intended state until the page goes; do not re-add a sync.

    // Broadcast to other tabs
    broadcastSettings(patch)

    // Mirror the theme locally. This is what lets an ANONYMOUS visitor's choice survive a
    // reload at all (their PATCH 401s and savePatch swallows it), and for everyone else
    // it simply keeps the device record in step with ThemeProvider's bg cache.
    if (patch.theme) writeLocalTheme(patch.theme)

    setSaving(true)
    await savePatch(patch)
    setSaving(false)
  }, [broadcastSettings])

  // Update a single setting
  const updateSetting = useCallback(<K extends keyof PublicSettings>(
    key: K,
    value: PublicSettings[K]
  ) => applyPatch({ [key]: value } as Partial<PublicSettings>), [applyPatch])

  // Update multiple settings at once
  const updateSettings = useCallback(
    (updates: Partial<PublicSettings>) => applyPatch(updates),
    [applyPatch],
  )

  /**
   * Reset to defaults.
   *
   * The ONE legitimately whole-object write, and it stays one: "put everything
   * back" is a statement about every key, so sending only what currently
   * differs would leave a key another tab changed mid-reset untouched.
   */
  const resetSettings = useCallback(() => applyPatch({ ...defaultSettings }), [applyPatch])

  return {
    settings,
    loading,
    saving,
    updateSetting,
    updateSettings,
    resetSettings,
  }
}
